import { IPublicClientApplication } from '@azure/msal-browser'
import { protectedResources } from '../authConfig'
import { AllLocationsPricingRow } from '../models/Admin/AllLocationsPricingRow'
import { CouponCode } from '../models/Admin/CouponCode'
import { InactiveDevice } from '../models/Admin/InactiveDevice'
import { LocationReport } from '../models/Admin/LocationReport'
import { TransactionPendingApproval } from '../models/Admin/PendingApprovalItems'
import { UpdateApprovalStatusDto } from '../models/Admin/UpdateApprovalStatusDto'
import {
  DesignRequest,
  DesignRequestMessage,
  FileWithUrl,
} from '../models/DesignRequest'
import {
  authorizedDelete,
  authorizedGet,
  authorizedPatch,
  authorizedPost,
  authorizedPut,
} from '../utils/authUtils'

const baseUrl = process.env.REACT_APP_BASE_API_URL

export const getTransactionsPendingContentCheck = async (
  instance: IPublicClientApplication,
): Promise<TransactionPendingApproval[]> => {
  return await authorizedGet(
    instance,
    `${baseUrl}/admin/pendingContentCheck`,
    protectedResources.scopes.read,
  )
}

export const updateApprovalStatuses = async (
  updateApprovalStatuses: UpdateApprovalStatusDto,
  instance: IPublicClientApplication,
): Promise<void> => {
  await authorizedPost(
    instance,
    `${baseUrl}/admin/updateContentStatus`,
    updateApprovalStatuses,
    protectedResources.scopes.read,
  )
}

export const getAdsPendingDesign = async (
  instance: IPublicClientApplication,
): Promise<DesignRequest[]> => {
  return await authorizedGet(
    instance,
    `${baseUrl}/admin/pendingDesign`,
    protectedResources.scopes.read,
  )
}

export const getDesignRequestById = async (
  instance: IPublicClientApplication,
  id: number,
): Promise<DesignRequest> => {
  return await authorizedGet(
    instance,
    `${baseUrl}/admin/designRequests/${id}`,
    protectedResources.scopes.read,
  )
}

export const sendAdminMessage = async (
  instance: IPublicClientApplication,
  requestId: number,
  message: string,
  files: FileWithUrl[],
): Promise<DesignRequestMessage> => {
  return await authorizedPost(
    instance,
    `${baseUrl}/admin/designRequests/${requestId}/messages`,
    { message, files },
    protectedResources.scopes.read,
  )
}

export const updateAdImageUrl = async (
  adId: number,
  imageUrl: string,
  instance: IPublicClientApplication,
): Promise<void> => {
  await authorizedPut(
    instance,
    `${baseUrl}/admin/updateAdImage`,
    {
      imageUrl,
      advertisementId: adId,
    },
    protectedResources.scopes.read,
  )
}

export const fetchLocationReports = async (
  instance: IPublicClientApplication,
  status?: string | null,
): Promise<LocationReport[]> => {
  const url = new URL(`${baseUrl}/admin/reports`)
  if (status) {
    url.searchParams.append('Status', status)
  }
  return authorizedGet(instance, url.toString(), protectedResources.scopes.read)
}

export const updateReportStatus = async (
  reportId: number,
  status: string,
  instance: IPublicClientApplication,
): Promise<void> => {
  return authorizedPatch(
    instance,
    `${baseUrl}/admin/reports/${reportId}/status`,
    { status },
    protectedResources.scopes.read,
  )
}

export const getInactiveDevices = async (
  instance: IPublicClientApplication,
  daysInactive: number = 7,
): Promise<InactiveDevice[]> => {
  const url = new URL(`${baseUrl}/admin/inactiveDevices`)
  url.searchParams.append('daysInactive', daysInactive.toString())
  return authorizedGet(instance, url.toString(), protectedResources.scopes.read)
}

export const listCouponCodes = async (
  instance: IPublicClientApplication,
): Promise<CouponCode[]> => {
  return authorizedGet(instance, `${baseUrl}/admin/coupon/list`, protectedResources.scopes.read)
}

export const createCouponCode = async (
  instance: IPublicClientApplication,
  code: string,
  expiresAt?: Date | null,
): Promise<CouponCode> => {
  return authorizedPost(
    instance,
    `${baseUrl}/admin/coupon/create`,
    { code, expiresAt: expiresAt?.toISOString() ?? null },
    protectedResources.scopes.read,
  )
}

export const deactivateCouponCode = async (
  instance: IPublicClientApplication,
  id: number,
): Promise<CouponCode> => {
  return authorizedPut(
    instance,
    `${baseUrl}/admin/coupon/${id}/deactivate`,
    {},
    protectedResources.scopes.read,
  )
}

export const fetchAllLocationsPricingRows = async (
  instance: IPublicClientApplication,
): Promise<AllLocationsPricingRow[]> => {
  return authorizedGet(
    instance,
    `${baseUrl}/AllLocationsPricing`,
    protectedResources.scopes.read,
  )
}

export const createAllLocationsPricingRow = async (
  instance: IPublicClientApplication,
  input: { numberOfDays: number; price: number; effectiveDate: string },
): Promise<AllLocationsPricingRow> => {
  return authorizedPost(
    instance,
    `${baseUrl}/AllLocationsPricing`,
    input,
    protectedResources.scopes.read,
  )
}

export const deleteAllLocationsPricingRow = async (
  id: number,
  instance: IPublicClientApplication,
): Promise<void> => {
  return authorizedDelete(
    instance,
    `${baseUrl}/AllLocationsPricing/${id}`,
    protectedResources.scopes.read,
  )
}
